/**
 * Eigene Daten im Kundenportal.
 *
 * Der Bauhof soll seinen Anzeigenamen und sein Passwort selbst ändern können,
 * ohne im Prüfbüro anzurufen.
 *
 * Was er nicht ändern kann, und warum: die **E-Mail-Adresse** ist die
 * Anmeldekennung – wer sie ändern dürfte, könnte sich selbst aussperren. Und
 * die **Kundenzuordnung** entscheidet, welche Spielplätze er sieht; sie zu
 * ändern hieße, sich fremde Daten zu verschaffen. Beides bleibt beim
 * Prüfbüro.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/klient'
import { Blatt } from '../bausteine/Blatt'
import { Zweitfaktor } from '../bausteine/Zweitfaktor'
import { Fehlerhinweis } from '../bausteine/Hinweise'
// Der Typ gehört zum Portal; ihn hier nachzubauen hieße, ihn irgendwann zu
// vergessen, wenn dort ein Feld dazukommt.
import type { PortalKonto } from './Portal'

export function Profil({
  konto,
  schliessen,
  aktualisiert,
}: {
  konto: PortalKonto
  schliessen: () => void
  aktualisiert: (neu: PortalKonto) => void
}) {
  const [name, setzeName] = useState(konto.name)
  const [gespeichert, setzeGespeichert] = useState(false)
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)

  useEffect(() => setzeName(konto.name), [konto.name])

  async function namenSpeichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    setzeFehler(null)
    setzeGespeichert(false)
    setzeLaeuft(true)
    try {
      const neu = await api.aendern<PortalKonto>('/api/portal/ich', {
        name: name.trim(),
      })
      aktualisiert(neu)
      setzeGespeichert(true)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Blatt titel="Meine Daten" untertitel={konto.kunde_name} schliessen={schliessen}>
      <div className="space-y-5">
        <div className="karte bg-slate-50">
          <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[9rem_1fr]">
            <dt className="font-semibold">E-Mail</dt>
            <dd>{konto.email}</dd>
            <dt className="font-semibold">Einrichtung</dt>
            <dd>{konto.kunde_name}</dd>
          </dl>
          <p className="mt-2 text-base text-slate-600">
            Adresse und Zuordnung ändert das Prüfbüro. Die Adresse ist Ihre
            Anmeldekennung.
          </p>
        </div>

        <form className="space-y-3" onSubmit={namenSpeichern}>
          <div>
            <label htmlFor="p-name">Name</label>
            <input
              id="p-name"
              value={name}
              onChange={(e) => setzeName(e.target.value)}
              required
              maxLength={120}
            />
            <p className="mt-1 text-base text-slate-600">
              Erscheint beim Prüfbüro an Ihren Meldungen.
            </p>
          </div>

          <Fehlerhinweis fehler={fehler} />

          <div className="flex items-center gap-3">
            <button type="submit" className="knopf-haupt" disabled={laeuft}>
              {laeuft ? 'Speichert …' : 'Speichern'}
            </button>
            {gespeichert && (
              <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
                Gespeichert
              </span>
            )}
          </div>
        </form>

        <Passwortwechsel />

        <Zweitfaktor pfad="/api/portal/zweitfaktor" />
      </div>
    </Blatt>
  )
}

function Passwortwechsel() {
  const [alt, setzeAlt] = useState('')
  const [neu, setzeNeu] = useState('')
  const [wiederholung, setzeWiederholung] = useState('')
  const [laeuft, setzeLaeuft] = useState(false)
  const [fertig, setzeFertig] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)

  // Die Wiederholung prüft die Oberfläche selbst: der Server kennt nur ein
  // Feld, ein Tippfehler fiele sonst erst beim nächsten Anmelden auf.
  const passtNicht = wiederholung.length > 0 && neu !== wiederholung
  const zuKurz = neu.length > 0 && neu.length < 12

  async function absenden(ereignis: FormEvent) {
    ereignis.preventDefault()
    setzeFehler(null)
    setzeFertig(false)
    setzeLaeuft(true)
    try {
      await api.senden('/api/portal/passwort', {
        altes_passwort: alt,
        neues_passwort: neu,
      })
      setzeAlt('')
      setzeNeu('')
      setzeWiederholung('')
      setzeFertig(true)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <form className="space-y-3 border-t-2 border-slate-200 pt-4" onSubmit={absenden}>
      <h3 className="text-lg font-bold">Passwort ändern</h3>

      <div>
        <label htmlFor="p-alt">Bisheriges Passwort</label>
        <input
          id="p-alt"
          type="password"
          autoComplete="current-password"
          value={alt}
          onChange={(e) => setzeAlt(e.target.value)}
          required
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="p-neu">Neues Passwort</label>
          <input
            id="p-neu"
            type="password"
            autoComplete="new-password"
            value={neu}
            onChange={(e) => setzeNeu(e.target.value)}
            required
            minLength={12}
          />
          <p className="mt-1 text-base text-slate-600">Mindestens 12 Zeichen.</p>
        </div>
        <div>
          <label htmlFor="p-wdh">Wiederholung</label>
          <input
            id="p-wdh"
            type="password"
            autoComplete="new-password"
            value={wiederholung}
            onChange={(e) => setzeWiederholung(e.target.value)}
            required
            aria-invalid={passtNicht}
          />
          {passtNicht && (
            <p role="alert" className="mt-1 text-base font-semibold text-stufe-3">
              Die beiden Eingaben sind nicht gleich.
            </p>
          )}
        </div>
      </div>

      <Fehlerhinweis fehler={fehler} />

      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="knopf-haupt"
          disabled={laeuft || passtNicht || zuKurz || !alt || !neu}
        >
          {laeuft ? 'Ändert …' : 'Passwort ändern'}
        </button>
        {fertig && (
          <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
            Passwort geändert
          </span>
        )}
      </div>

      <p className="text-base text-slate-600">
        Andere Geräte, auf denen dieses Konto angemeldet ist, werden dabei
        abgemeldet. Dieses hier bleibt angemeldet.
      </p>
    </form>
  )
}
